/*=========================================================================================
  File Name: getters.js
  Description: Vuex Store - getters
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

// added so later we can keep breakpoint in sync automatically using this config file
// import tailwindConfig from "../../tailwind.config.js"

import AuthService from "@/services/authService";
import mapKeys from "lodash/mapKeys";

/**
 * 機能権限ツリーをフラットな Map { [code]: permission } に変換する
 */
const flattenPermissions = (permissions, map = {}) => {
  for (const p of permissions) {
    map[p.code] = p;
    if (p.children && p.children.length) {
      flattenPermissions(p.children, map);
    }
  }
  return map;
};

const getters = {
  // COMPONENT
  // vx-autosuggest
  starredPages: state =>
    state.navbarSearchAndPinList.data.filter(page => page.highlightAction),
  windowBreakPoint: state => {
    // This should be same as tailwind. So, it stays in sync with tailwind utility classes
    if (state.windowWidth >= 1200) return "xl";
    else if (state.windowWidth >= 992) return "lg";
    else if (state.windowWidth >= 768) return "md";
    else if (state.windowWidth >= 576) return "sm";
    else return "xs";
  },

  scrollbarTag: state => {
    return state.is_touch_device ? "div" : "VuePerfectScrollbar";
  },

  getMenuItems: (_state, { isPaid, featurePermissionMap }) => async menuItems => {
    const permMap = featurePermissionMap;
    const hasPermissions = Object.keys(permMap).length > 0;

    // featureCode が Permission ツリーに存在する項目は Permission 優先（Addon 判定をスキップ）。
    const isManagedByPermission = item =>
      hasPermissions && item.featureCode && Boolean(permMap[item.featureCode]);

    // --- 1. Addon ベースのフィルタリング（Permission 管理対象は素通し） ---
    const hasEcoShip = await AuthService.hasAddon(
      AuthService.ADDON_IDS.ECO_SHIP
    );
    let addons = await AuthService.getAddons();
    addons = hasEcoShip
      ? addons.filter(s => !(s.id === AuthService.ADDON_IDS.SECURE_DATAFLOW))
      : addons;

    let result = menuItems.filter(function(value) {
      if (isManagedByPermission(value)) return value;
      if (!value.isAddon) return value;
      for (let item in this) {
        if (this[item].name === value.slug) return value;
      }
    }, addons);

    // --- 2. isPaid フィルタ（既存挙動） ---
    result = isPaid
      ? result
      : result.map(item => {
          const isDisabled = checkDisabled(isPaid)(item.isFreeContent);
          return {
            ...item,
            isDisabled,
            localIcon:
              (isDisabled && item.disabledLocalUrl !== undefined) ||
              item.localIcon,
            localUrl: getLocalUrl(isDisabled)(item.disabledLocalUrl)(
              item.localUrl
            ),
          };
        });

    // --- 3. CREW tier フィルタ（既存挙動。Permission 管理対象は除外しない） ---
    const { CREW_TIER1, CREW_TIER2, CREW_TIER3 } = AuthService.ADDON_IDS;
    const tiers = addons.filter(addon =>
      [CREW_TIER1, CREW_TIER2, CREW_TIER3].includes(addon.id)
    );
    const isExistingUser = tiers && tiers.length <= 0;
    if (isExistingUser) {
      result = result.filter(
        item => item.slug !== "crews" || isManagedByPermission(item)
      );
    }

    // --- 4. Permission API による上書き判定（Permission 優先） ---
    if (hasPermissions) {
      result = result
        .map(item => {
          const code = item.featureCode;
          const perm = code ? permMap[code] : null;
          if (!perm) return item;

          // enterprise_enabled=false → サイドメニューから除外
          if (!perm.enterprise_enabled) {
            return null;
          }

          // enterprise_enabled=true, user_enabled=false → グレーアウト
          const isDisabled = !perm.user_enabled;
          return {
            ...item,
            isDisabled,
            localIcon:
              (isDisabled && item.disabledLocalUrl !== undefined) ||
              item.localIcon,
            localUrl:
              isDisabled && item.disabledLocalUrl
                ? item.disabledLocalUrl
                : item.localUrl,
          };
        })
        .filter(item => item !== null);
    }

    return result;
  },

  isPaid: state => {
    return state.isFree !== true;
  },

  /**
   * Map the valid addons by their ID.
   *
   * @param {Object} state - The state object.
   * @param {Array} state.addons - The array of addons.
   * @returns {Object} - The object mapping addons by their ID. { [id]: addon }
   */
  addonMap: ({ addons }) => {
    if (!Array.isArray(addons)) return {};
    const addonMap = mapKeys(addons, "id");
    return addonMap;
  },

  /**
   * 機能権限ツリーを code をキーにしたフラット Map に変換する
   * { [code]: { code, enterprise_enabled, user_enabled, ... } }
   */
  featurePermissionMap: ({ featurePermissions }) => {
    if (!Array.isArray(featurePermissions) || featurePermissions.length === 0) return {};
    return flattenPermissions(featurePermissions);
  },
};

const checkDisabled = isPaid => isFreeContent => !(isPaid || isFreeContent);
const getLocalUrl = isDisabled => disabledLocalUrl => localUrl =>
  isDisabled && disabledLocalUrl ? disabledLocalUrl : localUrl;

export default getters;
